import { isEqual, isNull } from 'lodash/fp';
import { toast } from 'react-toastify';
import { push } from 'redux-first-history';
import { all, call, debounce, fork, put, select, takeLeading, takeLatest } from 'redux-saga/effects';

import { fetchAgreementTypeQueries, fetchAIAgreementTypes, fetchDocumentQueryHistory, updateAgreementTypeQueries, updateAgreementTypeQueriesOrder } from '../../../../../services/ai';
import { fetchAgreementTypeQueriesFailed, fetchAgreementTypeQueriesStarted, fetchAgreementTypeQueriesSuccessful, fetchAIAgreementTypesFailed, fetchAIAgreementTypesStarted, fetchAIAgreementTypesSuccessful, fetchDocumentQueryHistoryFailed, fetchDocumentQueryHistoryStarted, fetchDocumentQueryHistorySuccessful, saveQueryOrderFailed, saveQueryOrderSuccessful, saveUpdatedQueriesFailed, saveUpdatedQueriesSuccessful, setDocumentQueriesUpdated, setLastUpdatedOrder, setSelectedAIAgreementType } from './actions';
import { getCurrentDocumentQueries, getSavedDocumentQueries, getSelectedAIAgreementTypeId, getSelectedClientId, getSelectedHistoricalDocumentQueryId, getUpdatedQueries, getUpdatedQueryOrder } from './selectors';
import { AdminAIQueriesActionTypes, AIAgreementType, DocumentQuery, DocumentQueryDB, PortfolioTotal, SystemIdInfo, UpdatedQueryOrder } from './types';
import { AdminAIPlaygroundActionTypes } from '../../playground/store';

export function* attemptFetchAIAgreementTypes() {
    try {
        const clientId: number | null = yield select(getSelectedClientId);
        const { agreementTypes, portfolioTotal }: { agreementTypes: AIAgreementType[]; portfolioTotal: PortfolioTotal; } = yield call(fetchAIAgreementTypes, { clientId });
        yield put(fetchAIAgreementTypesSuccessful(agreementTypes, portfolioTotal));
    } catch (e) {
        yield put(fetchAIAgreementTypesFailed((e as Error).message));
    }
}

function* fetchAIAgreementTypesWatcher() {
    yield takeLatest([AdminAIQueriesActionTypes.FETCH_AI_AGREEMENT_TYPES_STARTED, AdminAIQueriesActionTypes.SET_AI_QUERIES_SELECTED_CLIENT], attemptFetchAIAgreementTypes);
}

export function* updateAISelectedClient() {
    yield put(fetchAIAgreementTypesStarted());
    const selectedAgreementTypeId: number | null = yield select(getSelectedAIAgreementTypeId);
    if (!isNull(selectedAgreementTypeId)) {
        yield put(fetchAgreementTypeQueriesStarted(selectedAgreementTypeId, false));
    }
}

function* setAIQueriesSelectedClientWatcher() {
    yield takeLeading(AdminAIQueriesActionTypes.SET_AI_QUERIES_SELECTED_CLIENT, updateAISelectedClient);
}

export function* attemptFetchAgreementTypeQueries({ payload }: ReturnType<typeof fetchAgreementTypeQueriesStarted>) {
    try {
        const { agreementTypeId, shouldRedirect } = payload;
        yield put(setSelectedAIAgreementType(agreementTypeId, shouldRedirect));
        const clientId: number | null = yield select(getSelectedClientId);
        const { queries, systemInfo }: { queries: DocumentQuery[]; systemInfo: SystemIdInfo[]; } = yield call(fetchAgreementTypeQueries, { agreementTypeId, clientId });
        yield put(fetchAgreementTypeQueriesSuccessful(queries, systemInfo));
    } catch (e) {
        yield put(fetchAgreementTypeQueriesFailed((e as Error).message));
    }
}

function* fetchAgreementTypeQueriesWatcher() {
    yield takeLatest(AdminAIQueriesActionTypes.FETCH_AGREEMENT_TYPE_QUERIES_STARTED, attemptFetchAgreementTypeQueries);
}

export function* redirectAgreementTypeId({ payload }: ReturnType<typeof setSelectedAIAgreementType>) {
    const { shouldRedirect, agreementTypeId } = payload;
    if (shouldRedirect) {
        let navigationPath = '/system/ai/data-extraction';
        if (!isNull(payload.agreementTypeId)) {
            navigationPath = `/system/ai/data-extraction/${agreementTypeId}`;
        }
        yield put(push(navigationPath));
    }
}

function* redirectAIAgreementTypeWatcher() {
    yield takeLatest(AdminAIQueriesActionTypes.SET_SELECTED_AI_AGREEMENT_TYPE, redirectAgreementTypeId);
}

export function* attemptUpdateAgreementTypeQueriesOrder() {
    try {
        const agreementTypeId: number = yield select(getSelectedAIAgreementTypeId);
        const updatedQueryOrder: UpdatedQueryOrder[] = yield select(getUpdatedQueryOrder);
        const clientId: number | null = yield select(getSelectedClientId);
        const queries: DocumentQuery[] = yield call(updateAgreementTypeQueriesOrder, { updatedQueryOrder, agreementTypeId, clientId });
        yield put(saveQueryOrderSuccessful(queries));
    } catch (e) {
        toast.error('Unable to reorder queries. Please try again.');
        yield put(saveQueryOrderFailed((e as Error).message));
    }
}

function* updateAgreementTypeQueriesOrderWatcher() {
    yield takeLeading(AdminAIQueriesActionTypes.SAVE_QUERY_ORDER_STARTED, attemptUpdateAgreementTypeQueriesOrder);
}

export function* checkDocumentQueriesUpdated() {
    const currentQueries: DocumentQuery[] = yield select(getCurrentDocumentQueries);
    const savedQueries: DocumentQuery[] = yield select(getSavedDocumentQueries);
    const haveUpdated = !isEqual(currentQueries, savedQueries);
    yield put(setDocumentQueriesUpdated(haveUpdated));
}

function* checkDocumentQueriesUpdatedWatcher() {
    yield debounce(200, [AdminAIQueriesActionTypes.ADD_NEW_DOCUMENT_QUERY, AdminAIQueriesActionTypes.UPDATE_DOCUMENT_QUERY, AdminAIQueriesActionTypes.DELETE_DOCUMENT_QUERY, AdminAIQueriesActionTypes.UPDATE_DOCUMENT_QUERY_SYSTEM_INFO], checkDocumentQueriesUpdated);
}

export function* attemptUpdateAgreementTypeQueries() {
    try {
        const agreementTypeId: number = yield select(getSelectedAIAgreementTypeId);
        const updatedQueries: DocumentQuery[] = yield select(getUpdatedQueries);
        const clientId: number | null = yield select(getSelectedClientId);
        const { agreementTypes, portfolioTotal, queries }: { agreementTypes: AIAgreementType[]; portfolioTotal: PortfolioTotal; queries: DocumentQuery[]; } = yield call(updateAgreementTypeQueries, { updatedQueries, agreementTypeId, clientId });
        yield put(saveUpdatedQueriesSuccessful(agreementTypes, portfolioTotal, queries));
        const lastUpdatedOrder = updatedQueries.length > 0 ? updatedQueries[0].queryOrder : null;
        yield put(setLastUpdatedOrder(lastUpdatedOrder));
        const selectedHistoricalId: number | null = yield select(getSelectedHistoricalDocumentQueryId);
        if (selectedHistoricalId) {
            // If we have a selected historical id we know that that there will be max 1 updated field and it will have an originalQueryId
            const originalQueryId: number = updatedQueries[0].originalQueryId!;
            yield put(fetchDocumentQueryHistoryStarted(originalQueryId));
        }
    } catch (e) {
        toast.error('Unable to update queries. Please try again.');
        yield put(saveUpdatedQueriesFailed((e as Error).message));
    }
}

function* updateAgreementTypeQueriesWatcher() {
    yield takeLeading(AdminAIQueriesActionTypes.SAVE_UPDATED_QUERIES_STARTED, attemptUpdateAgreementTypeQueries);
}

export function* attemptFetchDocumentQueryHistory({ payload }: ReturnType<typeof fetchDocumentQueryHistoryStarted>) {
    try {
        const documentQueryHistory: DocumentQueryDB[] = yield call(fetchDocumentQueryHistory, { originalQueryId: payload });
        yield put(fetchDocumentQueryHistorySuccessful(documentQueryHistory));
    } catch (e) {
        yield put(fetchDocumentQueryHistoryFailed((e as Error).message));
    }
}

function* fetchDocumentQueryHistoryWatcher() {
    yield takeLatest([AdminAIQueriesActionTypes.FETCH_DOCUMENT_QUERY_HISTORY_STARTED], attemptFetchDocumentQueryHistory);
}

export function* resetSelectedAgreementType() {
    yield put(setSelectedAIAgreementType(null, false));
}

function* resetPlaygroundTestDocumentWatcher() {
    yield takeLeading([AdminAIPlaygroundActionTypes.RESET_ML_TEST_DOCUMENT], resetSelectedAgreementType);
}

export function* adminAIQueriesSaga() {
    yield all([
        fork(fetchAIAgreementTypesWatcher),
        fork(fetchAgreementTypeQueriesWatcher),
        fork(redirectAIAgreementTypeWatcher),
        fork(updateAgreementTypeQueriesOrderWatcher),
        fork(checkDocumentQueriesUpdatedWatcher),
        fork(updateAgreementTypeQueriesWatcher),
        fork(fetchDocumentQueryHistoryWatcher),
        fork(setAIQueriesSelectedClientWatcher),
        fork(resetPlaygroundTestDocumentWatcher)
    ]);
}
